export const ENTER_KEY_CODE = 13;
export const ESCAPE_KEY_CODE = 27;
export const SHIFT_KEY_CODE = 16;
export const SPACE_KEY_CODE = 32;
export const TAB_KEY_CODE = 9;
export const Z_KEY_CODE = 90;

export const NAVIGATION_KEY_CODES = {
  TAB: TAB_KEY_CODE,
  ENTER: ENTER_KEY_CODE,
  UP_ARROW: 38,
  DOWN_ARROW: 40,
};

// for KeyboardEvent.key (https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key) (KeyboardEvent.keyCode is deprecated)
export const KEY_VALUES = {
  ARROW_DOWN: `ArrowDown`,
  ARROW_UP: `ArrowUp`,
  ARROW_LEFT: `ArrowLeft`,
  ARROW_RIGHT: `ArrowRight`,
  BACKSPACE: `Backspace`,
  ENTER: `Enter`,
  ESCAPE: `Escape`,
  TAB: `Tab`,
};

export const KEY_EVENTS = {
  KEYUP: `keyup`,
};

export const MOUSE_EVENTS = {
  MOUSEDOWN: `mousedown`,
  MOUSEUP: `mouseup`,
  MOUSEMOVE: `mousemove`,
  MOUSEOVER: `mouseover`,
};

/**
 * Checks for presence of a slotted child element
 */
export function hasSlot(el, slotName) {
  return !!el.querySelector(`[slot="${slotName}"]`);
}

export const ANIMATION_END_EVENTS = [`webkitAnimationEnd`, `animationend`, `oAnimationEnd`, `MSAnimationEnd`];

export function eventWasOutside(ev, subjectEl) {
  // events from shadow DOM will be retargeted to appear as if they come from component
  // use path to distinguish between events within nested components
  const path = window.ShadowDOMPolyfill || typeof ev.composedPath !== `function` ? ev.path : ev.composedPath();
  return !path.includes(subjectEl);
}

export const onAnimationEnd = (el, callback) => {
  ANIMATION_END_EVENTS.forEach((ev) => el.addEventListener(ev, callback));
};

export const offAnimationEnd = (el, callback) => {
  ANIMATION_END_EVENTS.forEach((ev) => el.removeEventListener(ev, callback));
};

// browsers like MicrosoftEdge will fire both newer 'animationend' and legacy 'MSAnimationEnd'
// To preserve backwards compatibility we have a helper that only calls the callback once
export const onAnimationEndOnce = (el, callback) => {
  let called = false;
  function onceCallback(ev) {
    if (!called) {
      called = true;
      callback(ev);
      offAnimationEnd(el, onceCallback);
    }
  }
  onAnimationEnd(el, onceCallback);
};

/**
 * Returns the closest parent element matching the given selector,
 * or null if no match is found or the selector is falsy.
 */
export const getClosest = (el, selector) => {
  if (!selector) {
    return null;
  }

  let parent = el.parentNode;
  while (parent && parent !== document) {
    if (parent.matches(selector)) {
      return parent;
    }

    parent = parent.parentNode;
  }

  return null;
};

/*
 * Returns a promise that resolves when all keys and their predicates have been observed, or timeout occurs.
 */
export function assertMutationsOccurred({keys, predicateForKey, timeout, target}) {
  const results = {occurred: [], notOccurred: [...keys]};
  const markSuccessful = (key) => {
    results.occurred.push(key);
    results.notOccurred.splice(results.notOccurred.indexOf(key), 1);
  };

  return new Promise((resolve) => {
    const observer = new MutationObserver((records) => {
      const success = results.notOccurred.filter((key) => records.some(predicateForKey(key)));

      success.forEach(markSuccessful);

      if (results.notOccurred.length === 0) {
        observer.disconnect();
        resolve(results);
      }
    });

    observer.observe(target, {
      attributes: true,
      childlist: true,
      subtree: true,
    });

    setTimeout(() => {
      if (observer) {
        observer.disconnect();
      }

      resolve(results);
    }, timeout);
  });
}

/**
 * Scroll to vertically center an element in the window
 */
export function scrollToElement(element) {
  const rect = element.getBoundingClientRect();
  window.scrollTo(
    document.body.scrollLeft,
    document.body.scrollTop + rect.top - (window.innerHeight - rect.height) / 2,
  );
}

/**
 * Scroll element to align with the bottom of the viewport if currently cut-off.
 */
export function scrollIntoViewIfNeeded(element) {
  const {height, y} = element.getBoundingClientRect();

  if (height + y > window.innerHeight) {
    element.scrollIntoView(false);
  }
}

/**
 * Smooth scrolls an scrollable element to an x, y position
 */
export function elementSmoothScrollToPosition(elm, toX, toY) {
  let curX = elm.scrollLeft;
  let curY = elm.scrollTop;

  const step = 20;
  let xStep = step;
  let yStep = step;

  if (curX > toX) {
    xStep = -xStep;
  }
  if (curY > toY) {
    yStep = -yStep;
  }

  let smoothScroll = setInterval(function() {
    elm.scrollTo(curX, curY);

    const exitX = (xStep > 0 && curX >= toX) || (xStep < 0 && curX <= toX);
    const exitY = (yStep > 0 && curY >= toY) || (yStep < 0 && curY <= toY);

    if (!exitX) {
      curX += xStep;
    }

    if (!exitY) {
      curY += yStep;
    }

    if (exitX && exitY) {
      clearInterval(smoothScroll);
      smoothScroll = null;
    }
  }, step);
}

/**
 * Copy text from an input element
 */
export function copyInputElementText(el) {
  let succeeded = false;

  try {
    el.select();
    succeeded = document.execCommand(`Copy`);
  } catch (err) {
    succeeded = false;
  }

  return succeeded;
}

export function propagateEvent(el, ev) {
  ev.stopPropagation();
  el.dispatchEvent(new CustomEvent(ev.type, ev));
}

export function preventTracking(ev) {
  /**
  Sometimes we need to prevent duplicated track events. We'll key off the
   cancelable field to determine if we propagate these events or not.
   */
  if (ev.cancelable) {
    ev.stopPropagation();
  }
}

/**
 * Emit a `track` event in a format understood by ReportTracker.
 *
 * @param {HTMLElement} el element to dispatch the event from
 * @param {any} options
 * @param {any} props properties to track
 * @param {string?} eventPrefix optionally tag the event name with a [prefix]
 */
export function emitTrackEvent(el, {eventName, eventPrefix, props, cancelable = false} = {}) {
  eventPrefix = eventPrefix ? `[${eventPrefix}] ` : null;

  el.dispatchEvent(
    new CustomEvent(`track`, {
      bubbles: true,
      composed: true,
      cancelable,
      detail: {
        props,
        eventPrefix,
        eventName: eventPrefix ? `${eventPrefix}${eventName}` : eventName,
      },
    }),
  );
}

/**
 * @param {{[string]: Function}} keyHandlerMap key code definition to handler function map
 * @param {Object} options
 * @param {boolean} options.stopPropagation stops event bubbling
 *
 * @example
 *  document.addEventListener(`keydown`, ev => keyEventSwitcher(ev, {
 *    'Escape': handleEscape,
 *    'Alt+Shift+KeyF': handleFocus, // NOTE: modifier keys need to be in alphabetical order
 *    'ArrowLeft,ArrowRight': handleArrowNav,
 *  })
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code#Code_values
 */
export function keyEventSwitcher(keyHandlerMap, {stopPropagation = false} = {}) {
  // separate keyHandlerMap comma separated values into their own map
  const keyCodeMap = new Map();
  for (const keyCodeCsv of Object.keys(keyHandlerMap)) {
    for (const keyCode of keyCodeCsv.split(`,`)) {
      keyCodeMap.set(keyCode, keyHandlerMap[keyCodeCsv]);
    }
  }

  return function keyEventHandler(ev) {
    // get code with modifier keys in X+KeyY format
    const keyCodeParts = [];
    for (const modifier of [`Alt`, `Ctrl`, `Meta`, `Shift`]) {
      if (ev[`${modifier.toLowerCase()}Key`]) {
        keyCodeParts.push(modifier);
      }
    }
    keyCodeParts.push(ev.code);

    const keyCode = keyCodeParts.join(`+`);
    const handlerFn = keyCodeMap.get(keyCode);

    if (handlerFn) {
      if (stopPropagation) {
        ev.stopPropagation();
        ev.preventDefault();
      }

      handlerFn(ev);
    }
  };
}

export function getModifierKeys(ev) {
  return {alt: ev.altKey, ctrl: ev.ctrlKey, shift: ev.shiftKey, meta: ev.metaKey};
}

export function toAttributeString(val) {
  if (typeof val === `string`) {
    return val;
  }
  return JSON.stringify(val);
}

/**
 * A simple helper for creating nested dom elements with attributes
 * @param {string} name
 * @param {{[attr: string]: any}} attrs
 * @param {(string|HTMLElement)[]} children
 * @returns {HTMLElement}
 */
export function createElem(name, attrs = {}, children = []) {
  const elem = document.createElement(name);

  for (const [attr, val] of Object.entries(attrs || {})) {
    elem.setAttribute(attr, toAttributeString(val));
  }

  if (!Array.isArray(children)) {
    children = [children];
  }

  for (const child of children) {
    if (typeof child === `string`) {
      elem.appendChild(document.createTextNode(child));
    } else if (child instanceof Element) {
      elem.appendChild(child);
    } else {
      throw new Error(`${String(child)} cannot be appended as a child. Need string or Element`);
    }
  }

  return elem;
}

export function enterFullscreenMode(elem, enteredCallback, exitedCallback) {
  const exitFullscreenHandler = function() {
    if (
      !document[`fullscreenElement`] &&
      !document[`webkitIsFullscreen`] &&
      !document[`mozFullScreen`] &&
      !document[`msFullscreenElement`]
    ) {
      exitedCallback();
    }
  };

  try {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
      document.addEventListener(`fullscreenchange`, exitFullscreenHandler);
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      elem.mozRequestFullScreen();
      document.addEventListener(`mozfullscreenchange`, exitFullscreenHandler);
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari & Opera */
      elem.webkitRequestFullscreen();
      document.addEventListener(`webkitfullscreenchange`, exitFullscreenHandler);
    } else if (elem.msRequestFullscreen) {
      /* IE/Edge */
      elem.msRequestFullscreen();
      document.addEventListener(`MSFullscreenChange`, exitFullscreenHandler);
    }
    enteredCallback();
  } catch (err) {
    console.error(err);
  }
}

export function exitFullscreenMode(exitedCallback) {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else if (document[`webkitCancelFullScreen`]) {
    document[`webkitCancelFullScreen`]();
  }
  exitedCallback();
}
